import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';

import { auth } from '../../../firebase.js';
import SignupLinkDateTimeField from '../../../components/SignupLinkDateTimeField/SignupLinkDateTimeField.jsx';
import SignupLinkShare from '../../../components/SignupLinkShare/SignupLinkShare.jsx';
import {
    copenhagenInputToDate,
    dateToCopenhagenInput,
    formatCopenhagenDateTime,
    signupLinkDisplayState,
} from '../../../common/signupLink.js';
import {
    createSignupLink,
    SIGNUP_LINK_PERMISSIONS,
    SIGNUP_TARGETS,
    signupLinkLabel,
    signupLinkUrl,
    subscribeToSignupLinks,
    updateSignupLink,
} from '../../../common/signupLinkApi.js';

export const SIGNUP_LINK_STATE_LABELS = {
    scheduled: { label: 'Planlagt', color: 'info' },
    active: { label: 'Aktiv', color: 'success' },
    full: { label: 'Fuld', color: 'warning' },
    expired: { label: 'Udløbet', color: 'default' },
    revoked: { label: 'Tilbagekaldt', color: 'default' },
};

const DAY_MS = 24 * 60 * 60 * 1000;

const emptyDraft = () => {
    const now = new Date();
    return {
        label: '',
        target: 'app',
        start: dateToCopenhagenInput(now),
        end: dateToCopenhagenInput(new Date(now.getTime() + 30 * DAY_MS)),
        maxUsers: '10',
        permissions: Object.fromEntries(SIGNUP_LINK_PERMISSIONS.map(({ key }) => [key, false])),
    };
};

function validateDraft(draft) {
    if (!draft.start || !draft.end) return 'Angiv start og slut.';
    const startDate = copenhagenInputToDate(draft.start);
    const endDate = copenhagenInputToDate(draft.end);
    if (endDate <= startDate) return 'Slut skal ligge efter start.';
    if (endDate <= new Date()) return 'Slut skal ligge i fremtiden.';
    const maxUsers = Number(draft.maxUsers);
    if (!Number.isInteger(maxUsers) || maxUsers < 1) return 'Antal brugere skal være et helt tal på mindst 1.';
    return null;
}

const SignupLinkForm = ({ company, onCancel, onCreated }) => {
    const [draft, setDraft] = useState(emptyDraft);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const update = (changes) => setDraft((current) => ({ ...current, ...changes }));

    const submit = async () => {
        const validationError = validateDraft(draft);
        if (validationError) {
            setError(validationError);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await createSignupLink(
                company.id,
                {
                    label: draft.label.trim(),
                    target: draft.target,
                    startDate: copenhagenInputToDate(draft.start),
                    endDate: copenhagenInputToDate(draft.end),
                    maxUsers: Number(draft.maxUsers),
                    permissions: draft.permissions,
                },
                auth.currentUser?.uid,
            );
            onCreated();
        } catch (createError) {
            console.error('Could not create sign-up link', createError);
            setError('Linket kunne ikke oprettes.');
            setSaving(false);
        }
    };

    return (
        <div className="setup-form-section signup-link-form">
            <div className="setup-form-section-header">
                <h3>Nyt tilmeldingslink</h3>
            </div>
            <TextField
                label="Navn på linket (valgfrit)"
                placeholder="F.eks. Pakke 10 brugere – faktura 2026-118"
                value={draft.label}
                onChange={(event) => update({ label: event.target.value })}
                size="small"
                fullWidth
            />
            <RadioGroup
                row
                value={draft.target}
                onChange={(event) => update({ target: event.target.value })}
            >
                {Object.entries(SIGNUP_TARGETS).map(([value, { label }]) => (
                    <FormControlLabel key={value} value={value} control={<Radio />} label={`Tilmelding til ${label}`} />
                ))}
            </RadioGroup>
            <div className="signup-link-form-row">
                <SignupLinkDateTimeField
                    label="Start (dansk tid)"
                    value={draft.start}
                    onChange={(start) => update({ start })}
                />
                <SignupLinkDateTimeField
                    label="Slut (dansk tid)"
                    value={draft.end}
                    onChange={(end) => update({ end })}
                />
                <TextField
                    label="Antal brugere"
                    type="number"
                    value={draft.maxUsers}
                    onChange={(event) => update({ maxUsers: event.target.value })}
                    size="small"
                    inputProps={{ min: 1, step: 1 }}
                />
            </div>
            <div>
                {SIGNUP_LINK_PERMISSIONS.map(({ key, label }) => (
                    <FormControlLabel
                        key={key}
                        label={label}
                        control={
                            <Checkbox
                                checked={draft.permissions[key]}
                                onChange={(event) =>
                                    update({ permissions: { ...draft.permissions, [key]: event.target.checked } })
                                }
                            />
                        }
                    />
                ))}
            </div>
            {error && <Alert severity="error">{error}</Alert>}
            <div className="signup-link-form-actions">
                <Button onClick={onCancel} disabled={saving}>Annuller</Button>
                <Button variant="contained" onClick={submit} disabled={saving}>Opret link</Button>
            </div>
        </div>
    );
};

const SignupLinkRow = ({ company, link, now, onRevoke }) => {
    const state = signupLinkDisplayState(link, now);
    const stateLabel = SIGNUP_LINK_STATE_LABELS[state];
    // A revoked or expired link no longer signs anyone up, so it is not handed out as a QR code.
    const stillWorks = state !== 'revoked' && state !== 'expired';

    return (
        <div className="signup-link-row">
            <div className="signup-link-row-main">
                <strong>{signupLinkLabel(link)}</strong>
                <Chip size="small" label={stateLabel.label} color={stateLabel.color} />
                <span>{SIGNUP_TARGETS[link.target]?.label}</span>
            </div>
            <div className="signup-link-row-details">
                <span>
                    {formatCopenhagenDateTime(link.start_date)} – {formatCopenhagenDateTime(link.end_date)}
                </span>
                <span>{link.users_signed_up.length} / {link.max_users} brugere</span>
            </div>
            <SignupLinkShare
                url={signupLinkUrl(company.label, company.id, link.id)}
                fileName={`${company.label || company.id} ${signupLinkLabel(link)}`}
                showUrl={false}
                allowQr={stillWorks}
                extraActions={stillWorks && (
                    <Button size="small" color="error" startIcon={<BlockIcon />} onClick={() => onRevoke(link)}>
                        Tilbagekald
                    </Button>
                )}
            />
        </div>
    );
};

// Lists every sign-up link a company has had, and creates and revokes them. Extending a
// running link happens from its panel on the home page.
const SignupLinksDialog = ({ open, company, onClose }) => {
    const [links, setLinks] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [creating, setCreating] = useState(false);
    const [revoking, setRevoking] = useState(null);
    const [revokeError, setRevokeError] = useState(null);
    const now = new Date();

    useEffect(() => {
        if (!open || !company) return undefined;
        setLoadError(null);
        return subscribeToSignupLinks(
            company.id,
            (nextLinks) => setLinks(nextLinks.sort((left, right) => right.start_date - left.start_date)),
            (error) => {
                console.error('Could not load sign-up links', error);
                setLoadError('Tilmeldingslinks kunne ikke indlæses.');
            },
        );
    }, [open, company]);

    useEffect(() => {
        if (!open) setCreating(false);
    }, [open]);

    const startRevoke = (link) => {
        setRevokeError(null);
        setRevoking(link);
    };

    const confirmRevoke = async () => {
        try {
            await updateSignupLink(company.id, revoking.id, { revoked: true });
            setRevoking(null);
        } catch (error) {
            console.error('Could not revoke sign-up link', error);
            setRevokeError('Linket kunne ikke tilbagekaldes.');
        }
    };

    if (!company) return null;
    const hasWasteReceiver = Boolean(company.wasteReceiver);

    return (
        <>
            <Dialog open={open} onClose={onClose} className="setup-dialog" maxWidth="md" fullWidth>
                <DialogTitle>Tilmeldingslinks · {company.label || company.id}</DialogTitle>
                <DialogContent>
                    {loadError && <Alert severity="error">{loadError}</Alert>}
                    {!hasWasteReceiver && (
                        <Alert severity="info">
                            Virksomheden skal have en affaldsmodtager, før der kan oprettes tilmeldingslinks.
                        </Alert>
                    )}

                    {creating ? (
                        <SignupLinkForm
                            company={company}
                            onCancel={() => setCreating(false)}
                            onCreated={() => setCreating(false)}
                        />
                    ) : (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setCreating(true)}
                            disabled={!hasWasteReceiver}
                        >
                            Nyt link
                        </Button>
                    )}

                    <div className="signup-link-list">
                        {links.length === 0
                            ? <p className="empty-inline">Ingen tilmeldingslinks endnu.</p>
                            : links.map((link) => (
                                <SignupLinkRow
                                    key={link.id}
                                    company={company}
                                    link={link}
                                    now={now}
                                    onRevoke={startRevoke}
                                />
                            ))}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Luk</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(revoking)} onClose={() => setRevoking(null)} className="setup-dialog">
                <DialogTitle>Tilbagekald tilmeldingslink?</DialogTitle>
                <DialogContent>
                    <p>
                        {revoking && signupLinkLabel(revoking)} holder op med at virke med det samme.
                        Brugere, der allerede er tilmeldt, beholder deres adgang. Det kan ikke fortrydes.
                    </p>
                    {revokeError && <Alert severity="error">{revokeError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevoking(null)}>Annuller</Button>
                    <Button color="error" variant="contained" onClick={confirmRevoke}>Tilbagekald</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SignupLinksDialog;
